<template>
  <div class="multi-tabs">
    <div
      ref="scrollRef"
      class="multi-tabs-scroll"
      @wheel.prevent="onWheel"
    >
      <div
        v-for="tab in tabs"
        :key="tab.path"
        :ref="(el) => setTabRef(tab.path, el)"
        class="multi-tabs-item"
        :class="{ active: tab.path === activeKey }"
        @click="onClickTab(tab)"
        @contextmenu.prevent="onContextMenu($event, tab)"
      >
        <span class="multi-tabs-dot"></span>
        <span class="multi-tabs-title">{{ tab.title }}</span>
        <span
          v-if="tab.closable"
          class="multi-tabs-close"
          @click.stop="onClose(tab)"
        >
          <CloseOutlined />
        </span>
      </div>
    </div>

    <a-dropdown :trigger="['hover']" placement="bottomRight">
      <a class="multi-tabs-more" @click.prevent title="页签操作">
        <AppstoreOutlined />
      </a>
      <template #overlay>
        <a-menu @click="onMenuClick">
          <a-menu-item key="closeOther">关闭其他</a-menu-item>
          <a-menu-item key="closeAll">关闭全部</a-menu-item>
        </a-menu>
      </template>
    </a-dropdown>

    <div
      v-if="context.visible"
      class="multi-tabs-context"
      :style="{ left: context.x + 'px', top: context.y + 'px' }"
      @mouseleave="context.visible = false"
    >
      <div class="ctx-item" @click="onClose(context.tab)">关闭</div>
      <div class="ctx-item" @click="closeOther(context.tab)">关闭其他</div>
      <div class="ctx-item" @click="closeAll">关闭全部</div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { CloseOutlined, AppstoreOutlined } from '@ant-design/icons-vue'
import { tabsStore } from '@/store/tabs'
import { dropTabComponent } from '@/utils/tabComponent'

const router = useRouter()
const tabs = computed(() => tabsStore.state.tabs)
const activeKey = computed(() => tabsStore.state.activeKey)
const scrollRef = ref(null)
const tabRefs = new Map()

const context = reactive({
  visible: false,
  x: 0,
  y: 0,
  tab: null,
})

function setTabRef(path, el) {
  if (el) tabRefs.set(path, el)
  else tabRefs.delete(path)
}

/** 元素相对滚动容器的左偏移 */
function getOffsetInContainer(el, container) {
  return (
    el.getBoundingClientRect().left -
    container.getBoundingClientRect().left +
    container.scrollLeft
  )
}

/** 与容器是否有交集（能看见任意一点都算已出现） */
function isIntersecting(container, el) {
  if (!el) return true
  const c = container.getBoundingClientRect()
  const t = el.getBoundingClientRect()
  return t.right > c.left && t.left < c.right
}

/**
 * 只有左右邻「完全在屏外」才需要滑动去露出；
 * 左右邻只要已经能看见一点 → 不滚。
 * 滚动无动画，直接改 scrollLeft。
 */
function scrollTabIntoView(path) {
  const run = () => {
    const container = scrollRef.value
    const list = tabs.value
    const index = list.findIndex((t) => t.path === path)
    const target = tabRefs.get(path)
    if (!container || !target || index < 0) return

    const prev = index > 0 ? tabRefs.get(list[index - 1].path) : null
    const next = index < list.length - 1 ? tabRefs.get(list[index + 1].path) : null

    const currentVisible = isIntersecting(container, target)
    // 完全在屏外才算「需要露出」
    const needLeft = !!(prev && !isIntersecting(container, prev))
    const needRight = !!(next && !isIntersecting(container, next))

    // 当前可见，且左右邻都已能看见 → 绝不滚动
    // （20 个里可见约 10 个时，点最右侧倒数第 3 应走这里）
    if (currentVisible && !needLeft && !needRight) return

    const clientWidth = container.clientWidth
    let nextScroll = container.scrollLeft

    if (!currentVisible) {
      const tabLeft = getOffsetInContainer(target, container)
      const tabRight = tabLeft + target.offsetWidth
      if (tabLeft >= nextScroll + clientWidth) {
        nextScroll = tabRight - clientWidth
      } else {
        nextScroll = tabLeft
      }
    }

    if (needLeft) {
      nextScroll = Math.min(nextScroll, getOffsetInContainer(prev, container))
    }
    if (needRight) {
      const nextRight = getOffsetInContainer(next, container) + next.offsetWidth
      nextScroll = Math.max(nextScroll, nextRight - clientWidth)
    }

    const maxScroll = Math.max(0, container.scrollWidth - clientWidth)
    nextScroll = Math.min(Math.max(0, nextScroll), maxScroll)
    if (Math.abs(nextScroll - container.scrollLeft) < 1) return

    // 无动画
    container.scrollLeft = nextScroll
  }

  nextTick(() => requestAnimationFrame(run))
}

function onWheel(e) {
  const container = scrollRef.value
  if (!container) return
  if (container.scrollWidth <= container.clientWidth) return
  container.scrollLeft += e.deltaY || e.deltaX
}

function onClickTab(tab) {
  // 不在点击时滚动，等 activeKey 变化后再按左右邻规则处理，避免点可见项时误滑
  if (tab.path === activeKey.value) return
  router.push(tab.fullPath || tab.path)
}

function onClose(tab) {
  if (!tab?.closable) return
  dropTabComponent(tab.name)
  tabsStore.removeTab(tab.path)
  context.visible = false
}

function closeOther(tab) {
  if (!tab) return
  tabs.value
    .filter((t) => t.closable && t.path !== tab.path)
    .forEach((t) => dropTabComponent(t.name))
  tabsStore.removeOtherTabs(tab.path)
  context.visible = false
}

function closeAll() {
  tabs.value
    .filter((t) => t.closable)
    .forEach((t) => dropTabComponent(t.name))
  tabsStore.removeAllTabs()
  context.visible = false
}

function onMenuClick({ key }) {
  const current = tabs.value.find((t) => t.path === activeKey.value)
  if (key === 'closeOther' && current) closeOther(current)
  if (key === 'closeAll') closeAll()
}

function onContextMenu(e, tab) {
  context.visible = true
  context.x = e.clientX
  context.y = e.clientY
  context.tab = tab
}

function hideContext() {
  context.visible = false
}

watch(activeKey, (path) => {
  if (path) scrollTabIntoView(path)
})

watch(
  () => tabs.value.length,
  () => {
    if (activeKey.value) scrollTabIntoView(activeKey.value)
  }
)

onMounted(() => {
  document.addEventListener('click', hideContext)
  if (activeKey.value) scrollTabIntoView(activeKey.value)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', hideContext)
})
</script>

<style scoped>
.multi-tabs {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0 0;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  position: relative;
}
.multi-tabs-scroll {
  flex: 1;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 8px;
  padding-bottom: 1px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE */
}
.multi-tabs-scroll::-webkit-scrollbar {
  display: none; /* Chrome / Safari */
  width: 0;
  height: 0;
}
.multi-tabs-item {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 168px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: #f5f7fa;
  color: rgba(0, 0, 0, 0.65);
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 500; /* 固定字重，避免选中切换时宽度变化导致整条抖动 */
  transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}
.multi-tabs-item:hover {
  color: #1677ff;
  background: #e6f4ff;
}
.multi-tabs-item.active {
  color: #1677ff;
  background: #e6f4ff;
  border-color: #91caff;
}
.multi-tabs-item.active::after {
  content: '';
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: -1px;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background: #1677ff;
}
.multi-tabs-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c9cdd4;
  flex-shrink: 0;
  transition: all 0.18s ease;
}
.multi-tabs-item:hover .multi-tabs-dot,
.multi-tabs-item.active .multi-tabs-dot {
  background: #1677ff;
  box-shadow: 0 0 0 3px rgba(22, 119, 255, 0.15);
}
.multi-tabs-title {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  line-height: 1;
}
.multi-tabs-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.35);
  transition: all 0.15s ease;
}
.multi-tabs-close:hover {
  background: rgba(22, 119, 255, 0.12);
  color: #1677ff;
}
.multi-tabs-item.active .multi-tabs-close:hover {
  background: #fff;
}
.multi-tabs-more {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: rgba(0, 0, 0, 0.45);
  border-radius: 8px;
  background: #f5f7fa;
  border: 1px solid transparent;
  transition: all 0.15s ease;
}
.multi-tabs-more:hover {
  color: #1677ff;
  background: #e6f4ff;
  border-color: #91caff;
}
.multi-tabs-context {
  position: fixed;
  z-index: 1050;
  min-width: 128px;
  padding: 6px;
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.1);
}
.ctx-item {
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.75);
  font-size: 13px;
  transition: all 0.15s ease;
}
.ctx-item:hover {
  background: #e6f4ff;
  color: #1677ff;
}
</style>
